import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePWA } from '@/hooks/usePWA';

export function InstallPrompt() {
    const { isInstallable, isIOS, isAndroid, isMobile, promptInstall, isInstalled } = usePWA();
    const [showPrompt, setShowPrompt] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        // Check if already dismissed
        const wasDismissed = localStorage.getItem('publee_install_dismissed');
        if (wasDismissed) {
            const dismissedAt = new Date(wasDismissed);
            const daysSince = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
            // Show again after 7 days
            if (daysSince < 7) {
                setDismissed(true);
                return;
            }
        }

        // Show prompt after delay on mobile
        if (isMobile && !isInstalled && !dismissed) {
            const timer = setTimeout(() => {
                setShowPrompt(true);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [isMobile, isInstalled, dismissed]);

    const handleInstall = async () => {
        if (isInstallable) {
            const accepted = await promptInstall();
            if (accepted) {
                setShowPrompt(false);
            }
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        setDismissed(true);
        localStorage.setItem('publee_install_dismissed', new Date().toISOString());
    };

    // Don't show if not mobile, already installed, dismissed, or no prompt
    if (!showPrompt || isInstalled || !isMobile) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                className="fixed bottom-4 left-4 right-4 z-50"
            >
                <div className="bg-card border border-border rounded-xl shadow-2xl p-4">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary/20 rounded-lg shrink-0">
                            <Smartphone className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm">Instalar Publee</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                {isIOS
                                    ? 'Toque em "Compartilhar" e "Adicionar à Tela Inicial"'
                                    : 'Adicione à sua tela inicial para acesso rápido'}
                            </p>
                        </div>
                        <button
                            onClick={handleDismiss}
                            className="text-muted-foreground hover:text-foreground shrink-0"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {!isIOS && isInstallable && (
                        <Button
                            onClick={handleInstall}
                            className="w-full mt-3 gap-2"
                            size="sm"
                        >
                            <Download className="w-4 h-4" />
                            Instalar App
                        </Button>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
